import React, { useEffect } from "react";
import { Row, Col, Placeholder, Table, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { analyticsActions } from "../../actions/analytics.actions";

const getDateRange = (rangeType) => {
  const now = new Date();
  let startDate, endDate;

  switch (rangeType) {
    case "24hours":
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "lastWeek":
      // Get last Sunday
      const lastSunday = new Date(now);
      lastSunday.setDate(now.getDate() - now.getDay());
      lastSunday.setHours(0, 0, 0, 0);

      // Start of last week (Sunday 7 days before last Sunday)
      startDate = new Date(lastSunday);
      startDate.setDate(lastSunday.getDate() - 7);

      // End of last week (Saturday = last Sunday - 1 day)
      endDate = new Date(lastSunday);
      endDate.setDate(lastSunday.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
      break;
    case "lastMonth":
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case "oneMonth":
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "3months":
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "6months":
      startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case "ytd":
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = now;
      break;
    case "1year":
      startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    default:
      return { startDate: null, endDate: null };
  }

  // Add 1 day to endDate to include the entire end day (backend parses date-only strings as midnight)
  const adjustedEndDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: adjustedEndDate.toISOString().split('T')[0]
  };
};

function ApiPerformanceTab(props) {
  const dispatch = useDispatch();
  const analyticsReducer = useSelector((state) => state.analytics);

  useEffect(() => {
    if (!props.user) return;

    // Use clientId prop if provided (for super user viewing specific client),
    // otherwise use user's clientId
    const clientId = props.clientId || props.user.clientId;
    if (!clientId) return;

    const queryParams = {
      clientId: clientId,
      clientGuid: props.user.clientGuid,
    };

    const { startDate, endDate } = getDateRange(props.dateRange);
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    dispatch(analyticsActions.getApiUsage(queryParams));
    dispatch(analyticsActions.getApiFailures(queryParams));
    dispatch(analyticsActions.getApiRecords(queryParams));
    // eslint-disable-next-line
  }, [props.user, props.dateRange, props.clientId]);

  const apiUsage = analyticsReducer.apiUsage || [];
  const apiFailures = analyticsReducer.apiFailures || [];
  const apiRecords = analyticsReducer.apiRecords || [];
  const loading = analyticsReducer.loadingApiUsage || analyticsReducer.loadingApiFailures || analyticsReducer.loadingApiRecords;

  return (
    <div>
      {loading && (
        <Placeholder as={"div"} animation="glow">
          <Placeholder xs={12} size="lg" style={{ height: "400px" }} />
        </Placeholder>
      )}

      {!loading && (
        <>
          {/* Most Called API Systems */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Most Called API Systems</h5>
            </Card.Header>
            <Card.Body>
              {apiUsage.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>API System</th>
                      <th>Total Calls</th>
                      <th>Successful</th>
                      <th>Failed</th>
                      <th>Success Rate</th>
                      <th>Records Processed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiUsage.map((api, index) => (
                      <tr key={index}>
                        <td><strong>{api.apiSystem}</strong></td>
                        <td>{api.totalCalls.toLocaleString()}</td>
                        <td className="text-success">{api.successfulCalls.toLocaleString()}</td>
                        <td className="text-danger">{api.failedCalls.toLocaleString()}</td>
                        <td>
                          <span className={api.successRate >= 90 ? "text-success" : api.successRate >= 70 ? "text-warning" : "text-danger"}>
                            {api.successRate.toFixed(2)}%
                          </span>
                        </td>
                        <td>{api.totalRecordsProcessed.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-3">
                  <p>No API usage data available</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* API Failure Rates */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">APIs with Highest Failure Rates</h5>
            </Card.Header>
            <Card.Body>
              {apiFailures.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>API System</th>
                      <th>Object</th>
                      <th>Action</th>
                      <th>Total Calls</th>
                      <th>Failed</th>
                      <th>Failure Rate</th>
                      <th>Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiFailures.map((api, index) => (
                      <tr key={index}>
                        <td><strong>{api.apiSystem}</strong></td>
                        <td>{api.apiObject}</td>
                        <td>{api.apiAction}</td>
                        <td>{api.totalCalls.toLocaleString()}</td>
                        <td className="text-danger">{api.failedCalls.toLocaleString()}</td>
                        <td>
                          <span className={api.failureRate >= 30 ? "text-danger" : api.failureRate >= 10 ? "text-warning" : "text-success"}>
                            {api.failureRate.toFixed(2)}%
                          </span>
                        </td>
                        <td>{api.totalRecordsProcessed.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-3">
                  <p>No failure data available</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Records Processed by API */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Records Processed by API System</h5>
            </Card.Header>
            <Card.Body>
              {apiRecords.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>API System</th>
                      <th>Total Calls</th>
                      <th>Success Rate</th>
                      <th>Records Processed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiRecords.map((api, index) => (
                      <tr key={index}>
                        <td><strong>{api.apiSystem}</strong></td>
                        <td>{api.totalCalls.toLocaleString()}</td>
                        <td>
                          <span className={api.successRate >= 90 ? "text-success" : api.successRate >= 70 ? "text-warning" : "text-danger"}>
                            {api.successRate.toFixed(2)}%
                          </span>
                        </td>
                        <td><strong>{api.totalRecordsProcessed.toLocaleString()}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-3">
                  <p>No records data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}

export default ApiPerformanceTab;
